import { Stack, Typography } from '@mui/material';
import { SchoolPropertyType, SchoolUserRole } from '@schooly/constants';
import { useSchoolProperties } from '@schooly/hooks/use-school-properties';
import { Spin, TagSelectProperty } from '@schooly/style';
import { FC, PropsWithChildren, useCallback, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ExpandedSelect } from '../ExpandedSelect';
import { ExpandedSelectFooterButtons } from '../ExpandedSelectContentContainer';
import { SelectContentSkeleton } from '../SelectContentSkeleton';
import { SelectSearchInput } from '../SelectSearchInput';
import { filterPropertiesByQuery, toggleMultipleValueArrayProperty } from '../utils';
import { renderPropertyTypeTags } from './PropertyTypeExpandedSelect';

export const LABEL_TEXT_IDS = {
  [SchoolPropertyType.Status]: 'schoolProperty-Status-plural',
  [SchoolPropertyType.Department]: 'schoolProperty-Department-plural',
  [SchoolPropertyType.AgeGroup]: 'schoolProperty-AgeGroup-plural',
  [SchoolPropertyType.Campus]: 'schoolProperty-Campus-plural',
  [SchoolPropertyType.House]: 'schoolProperty-House-plural',
};

type StudentStaffPropertyTypeExpandedSelectProps = PropsWithChildren<{
  propertyType: SchoolPropertyType;
  schoolId: string;
  selectedStudentIds: string[];
  selectedStaffIds: string[];
  onApply: (studentIds: string[], staffIds: string[]) => void;
  onClear: () => void;
  onClose: () => void;
}>;
export const StudentStaffPropertyTypeExpandedSelect: FC<
  StudentStaffPropertyTypeExpandedSelectProps
> = ({
  propertyType,
  schoolId,
  selectedStudentIds: initialSelectedStudentIds,
  selectedStaffIds: initialSelectedStaffIds,
  onApply,
  onClose,
  onClear,
}) => {
  const { $t } = useIntl();
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>(initialSelectedStudentIds);
  const [selectedStaffIds, setSelectedStaffIds] = useState<string[]>(initialSelectedStaffIds);

  const {
    activePropertiesMap: staffActivePropertiesMap,
    archivedPropertiesMap: staffArchivedPropertiesMap,
    isLoading: staffPropertiesFetching,
  } = useSchoolProperties({ schoolId, userType: SchoolUserRole.Staff });
  const {
    activePropertiesMap: studentActivePropertiesMap,
    archivedPropertiesMap: studentArchivedPropertiesMap,
    isLoading: studentPropertiesFetching,
  } = useSchoolProperties({ schoolId, userType: SchoolUserRole.Student });

  const [query, setQuery] = useState('');

  const toggleStudentIds = useCallback((v: string[]) => {
    setSelectedStudentIds((ids) => toggleMultipleValueArrayProperty(ids, v));
  }, []);

  const toggleStaffIds = useCallback((v: string[]) => {
    setSelectedStaffIds((ids) => toggleMultipleValueArrayProperty(ids, v));
  }, []);

  const handleApply = useCallback(() => {
    onApply(selectedStudentIds, selectedStaffIds);
    onClose();
  }, [selectedStudentIds, selectedStaffIds, onApply, onClose]);

  const renderContent = useCallback(() => {
    if (studentPropertiesFetching || staffPropertiesFetching) return <SelectContentSkeleton />;

    const studentActiveProperties = filterPropertiesByQuery(
      query,
      studentActivePropertiesMap[propertyType],
    );

    const staffActiveProperties = filterPropertiesByQuery(
      query,
      staffActivePropertiesMap[propertyType],
    );

    const studentArchivedProperties = filterPropertiesByQuery(
      query,
      studentArchivedPropertiesMap[propertyType],
    );

    const staffArchivedProperties = filterPropertiesByQuery(
      query,
      staffArchivedPropertiesMap[propertyType],
    );

    if (
      !studentActiveProperties.length &&
      !staffActiveProperties.length &&
      !studentArchivedProperties.length &&
      !staffArchivedProperties.length
    )
      return (
        <Stack m={1} gap={2}>
          <Typography p={1}>
            <FormattedMessage id="input-NoOptionsFound" />
          </Typography>
          <ExpandedSelectFooterButtons onApply={handleApply} onClear={onClear} />
        </Stack>
      );

    return (
      <Stack m={1} gap={2}>
        {!!studentActiveProperties.length && (
          <Stack gap={1}>
            <Typography variant="h4">
              {$t({ id: 'userType-student' })} {$t({ id: LABEL_TEXT_IDS[propertyType] })}
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {studentActiveProperties.map((property) => (
                <TagSelectProperty
                  key={property.id}
                  userRole={SchoolUserRole.Student}
                  property={property}
                  defaultValue={<Spin />}
                  variant={selectedStudentIds?.includes(property.id) ? 'filled' : undefined}
                  onClick={() => {
                    toggleStudentIds([property.id]);
                    setQuery('');
                  }}
                />
              ))}
            </Stack>
          </Stack>
        )}
        {!!staffActiveProperties.length && (
          <Stack gap={1}>
            <Typography variant="h4">
              {$t({ id: 'userType-staff' })} {$t({ id: LABEL_TEXT_IDS[propertyType] })}
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {staffActiveProperties.map((property) => (
                <TagSelectProperty
                  key={property.id}
                  userRole={SchoolUserRole.Staff}
                  property={property}
                  defaultValue={<Spin />}
                  variant={selectedStaffIds?.includes(property.id) ? 'filled' : undefined}
                  onClick={() => {
                    toggleStaffIds([property.id]);
                    setQuery('');
                  }}
                />
              ))}
            </Stack>
          </Stack>
        )}
        {Boolean(!!studentArchivedProperties.length || !!staffArchivedProperties.length) && (
          <Stack gap={1}>
            <Typography variant="h4">
              <FormattedMessage id="filter-Archived" />
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {studentArchivedProperties.map((property) => (
                <TagSelectProperty
                  key={property.id}
                  userRole={SchoolUserRole.Student}
                  property={property}
                  defaultValue={<Spin />}
                  variant={selectedStudentIds?.includes(property.id) ? 'filled' : undefined}
                  onClick={() => {
                    toggleStudentIds([property.id]);
                    setQuery('');
                  }}
                />
              ))}
              {staffArchivedProperties.map((property) => (
                <TagSelectProperty
                  key={property.id}
                  userRole={SchoolUserRole.Staff}
                  property={property}
                  defaultValue={<Spin />}
                  variant={selectedStaffIds?.includes(property.id) ? 'filled' : undefined}
                  onClick={() => {
                    toggleStaffIds([property.id]);
                    setQuery('');
                  }}
                />
              ))}
            </Stack>
          </Stack>
        )}
        <ExpandedSelectFooterButtons onApply={handleApply} onClear={onClear} />
      </Stack>
    );
  }, [
    studentPropertiesFetching,
    staffPropertiesFetching,
    studentActivePropertiesMap,
    propertyType,
    staffActivePropertiesMap,
    studentArchivedPropertiesMap,
    staffArchivedPropertiesMap,
    $t,
    query,
    selectedStudentIds,
    toggleStudentIds,
    selectedStaffIds,
    toggleStaffIds,
    handleApply,
    onClear,
  ]);

  return (
    <ExpandedSelect
      hasSelectedValue={selectedStaffIds.length + selectedStudentIds.length > 0}
      onClose={onClose}
      onClear={onClear}
      onClickInputArea={() => inputRef.current?.focus()}
      renderContent={renderContent}
    >
      {renderPropertyTypeTags({
        ids: selectedStudentIds,
        onDelete: (id) => toggleStudentIds([id]),
        tagProps: {
          schoolId,
          userRole: SchoolUserRole.Student,
          sx: { maxWidth: 200 },
          size: 'small',
        },
      })}
      {renderPropertyTypeTags({
        ids: selectedStaffIds,
        onDelete: (id) => toggleStaffIds([id]),
        tagProps: {
          schoolId,
          userRole: SchoolUserRole.Staff,
          sx: { maxWidth: 200 },
          size: 'small',
        },
      })}
      <SelectSearchInput ref={inputRef} autoFocus value={query} onChangeText={setQuery} />
    </ExpandedSelect>
  );
};
